import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GROUP_ATTRIBUTION_CVE_SIDE_PANEL } from '../../../webcomponents.constants';
import _ from 'lodash';
import { translateFn } from '../../../utils/translator.helper';
import { INLINE_ACTIONS } from '../types';
import { GzSelectWebComponent } from '../../../ui/gzSelect';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { WebComponentEvent } from '../../../framework/BaseWebComponentController';
import { ColumnKeys as VulnerabilitiesColumnKeys } from '../../../riskManagement/vulnerabilitiesApp/types';

/**
 * @summary Side panel that contains CVE data
 * @tag groupAttributionCveSidePanel
 */
@customElement(WEB_COMPONENT_GROUP_ATTRIBUTION_CVE_SIDE_PANEL)
export class GroupAttributionCveSidePanelWebComponent extends LitElement {
  @property({
    type: Object,
    attribute: true,
    reflect: true,
  })
  _data;

  @property({
    type: String,
    attribute: true,
    reflect: true,
  })
  companyId = null;

  @state()
  private _currentData;

  static get styles() {
    return styles;
  }

  get data() {
    return this._data;
  }

  set data(prop) {
    const oldData = this._data;
    this._data = prop;
    this._currentData = _.cloneDeep(this._data);
    this.requestUpdate('data', oldData);
  }

  handleInputChange(event) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this._currentData = this.data.filter(cve => cve.toLowerCase().includes(searchInput));
  }

  getNumberOfCVEs() {
    return this._currentData.length;
  }

  constructor() {
    super();
  }

  private getSelectOptions() {
    return [
      {
        title: translateFn('View in Risk Management section'),
        value: INLINE_ACTIONS.RISK_MANAGEMENT_VULNERABILITIES,
      },
    ];
  }

  private getSelectTemplate() {
    return `<gz-icon-button icon="ds-icon-more" buttoncolor="secondary" buttontype="square"
        fontsize="22" size="24" buttonfill="quiet" class="inline-menu-trigger"></gz-icon-button>`;
  }

  renderInlineButton(cveValue: string) {
    const select = document.createElement('gz-select') as GzSelectWebComponent;
    select.options = this.getSelectOptions();
    select.innerHTML = this.getSelectTemplate();
    select.addEventListener(SelectEvents.SELECTION_CHANGED, async (event: WebComponentEvent) => {
      if (event.detail.selected === INLINE_ACTIONS.RISK_MANAGEMENT_VULNERABILITIES) {
        this.handlePivotToRiskManagement(cveValue);
      }
    });
    return select;
  }

  private handlePivotToRiskManagement(cveValue: string) {
    Application.lib.Navigation.openAppPageInNewTab(
      `/risk-management-vulnerabilities`,
      {
        filters: [
          {
            [VulnerabilitiesColumnKeys.CVES]: {
              complexEnumFilter: [{
                label: cveValue.toUpperCase(),
                value: cveValue.toUpperCase()
              }]
            }
          },
          {
            [VulnerabilitiesColumnKeys.COMPANY]: { exactMatch: this.companyId },
          }
        ]
      }
    );
  }

  render() {
    return html`
      <div class="header pb-4 pr-2">
        <gz-tooltip class="mb-2" tooltipText="${translateFn('Vulnerabilities details')}" isEllipsis>
          <gz-title class="d-inline" fontsize="16" fontWeight="500">${translateFn('Vulnerabilities details')}</gz-title>
        </gz-tooltip>

        <gz-input-search
          placeholder="${translateFn('Search CVE ID')}"
          @change="${this.handleInputChange}"
        ></gz-input-search>
      </div>
      <div class="content">
        <div class="container d-flex flex-column">
          <gz-labeled-content
            label="${translateFn('CVE ID') + ' (' + this.data.length + ')'}"
            labelFontSize="11"
            labelFontWeight="600"
          ></gz-labeled-content>
            ${this.renderCVEList()}
        </div>
      </div>
    `;
  }

  renderCVEList() {
    return this._currentData.map(
      cve => html`
        <div class="d-flex flex-row justify-content-between mt-1 mb-1">
          <gz-tooltip class="flex-1" tooltipText="${cve}" isEllipsis>
            <gz-text class="d-inline" fontSize="12">${cve}</gz-text>
          </gz-tooltip>
          ${this.renderInlineButton(cve)}
        </div>
      `
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    groupAttributionCveSidePanel: GroupAttributionCveSidePanelWebComponent;
  }
}
